'use client';

import { c as _c } from "react/compiler-runtime";
import { Button } from '@common/components';
import { useToast } from '@common/hooks';
import { useMutation } from '@tanstack/react-query';
import { delay } from 'es-toolkit';
import { AuthApi } from '@/api';
import { ENV, ROUTES, SEARCH_PARAMS } from '@/constants';
import { useAccessToken } from '@/hooks/useAccessToken';
import { useCheckWelloApp } from '@/hooks/useCheckWelloApp';
import { KB_PAY_CUSTOM_SCHEME } from '@/modules/kb-pay';
import { ANDROID_DEEPLINK, CUSTOM_SCHEME } from '@/modules/wello-app';
import { useDeviceInfoStore, WEBVIEW_TYPE } from '@/stores/DeviceStore';
import { closeResponsiveDialog } from '@/stores/DrawerStore';
export const KosafHouseholdConsentContent = () => {
  const $ = _c(27);
  const isKbWebview = useDeviceInfoStore(_temp);
  const {
    isWelloApp
  } = useCheckWelloApp();
  const {
    toast,
    toastError
  } = useToast();
  const {
    accessToken,
    refreshToken
  } = useAccessToken();
  const isIos = useDeviceInfoStore(_temp2) === "ios";
  let t0;
  if ($[0] !== isIos || $[1] !== isKbWebview || $[2] !== isWelloApp) {
    t0 = () => {
      const fallbackUrl = `${ENV.NEXT_PUBLIC_DOMAIN}${ROUTES.MY.pathname}`;
      const returnUrl = isWelloApp || isKbWebview ? isIos ? CUSTOM_SCHEME.WELLO_APP_PREFIX : `${CUSTOM_SCHEME.ANDROID_APP_PREFIX}${ANDROID_DEEPLINK}` : fallbackUrl;
      return AuthApi["get:/wello/v2/auth/kosaf/confirm-household-info-agreement"]({
        query: {
          redirectUrl: returnUrl,
          cancelUrl: returnUrl
        }
      });
    };
    $[0] = isIos;
    $[1] = isKbWebview;
    $[2] = isWelloApp;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  let t1;
  if ($[4] !== accessToken || $[5] !== isKbWebview || $[6] !== isWelloApp || $[7] !== refreshToken || $[8] !== toast) {
    t1 = async data => {
      const responseUrl = data.data?.context?.responseUrl;
      if (!responseUrl) {
        toast({
          message: "\uC694\uCCAD\uC5D0 \uD544\uC694\uD55C \uC815\uBCF4\uB97C \uAC00\uC838\uC624\uC9C0 \uBABB\uD588\uC2B5\uB2C8\uB2E4.",
          type: "fail"
        });
        return;
      }
      const confirmUrl = new URL(responseUrl);
      if (isWelloApp) {
        const searchParams = Object.fromEntries(confirmUrl.searchParams.entries());
        window.location.href = `${CUSTOM_SCHEME.IN_APP_EXTERNAL_PREFIX}${ENV.NEXT_PUBLIC_DOMAIN}${ROUTES.CROSS_DOMAIN_CALLBACK.withSearchParams({
          searchParams: {
            accessToken: accessToken as string,
            refreshToken: refreshToken as string,
            [SEARCH_PARAMS.REDIRECT]: confirmUrl.toString(),
            [SEARCH_PARAMS.REDIRECT_SEARCH_PARAMS]: JSON.stringify({
              ...searchParams
            })
          }
        })}`;
        await delay(1000);
        closeResponsiveDialog();
      } else {
        if (isKbWebview) {
          const searchParams_0 = Object.fromEntries(confirmUrl.searchParams.entries());
          window.location.href = `${KB_PAY_CUSTOM_SCHEME.EXTERNAL_PREFIX}${ENV.NEXT_PUBLIC_DOMAIN}${ROUTES.CROSS_DOMAIN_CALLBACK.withSearchParams({
            searchParams: {
              accessToken: accessToken as string,
              refreshToken: refreshToken as string,
              [SEARCH_PARAMS.REDIRECT]: confirmUrl.toString(),
              [SEARCH_PARAMS.REDIRECT_SEARCH_PARAMS]: JSON.stringify({
                ...searchParams_0
              })
            }
          })}`;
          await delay(1000);
          closeResponsiveDialog();
        } else {
          window.location.replace(confirmUrl.toString());
        }
      }
    };
    $[4] = accessToken;
    $[5] = isKbWebview;
    $[6] = isWelloApp;
    $[7] = refreshToken;
    $[8] = toast;
    $[9] = t1;
  } else {
    t1 = $[9];
  }
  let t2;
  if ($[10] !== toastError) {
    t2 = error => {
      toastError({
        error
      });
    };
    $[10] = toastError;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  let t3;
  if ($[12] !== t0 || $[13] !== t1 || $[14] !== t2) {
    t3 = {
      mutationFn: t0,
      onSuccess: t1,
      onError: t2
    };
    $[12] = t0;
    $[13] = t1;
    $[14] = t2;
    $[15] = t3;
  } else {
    t3 = $[15];
  }
  const {
    mutate: requestKosafConfirmUrl,
    isPending
  } = useMutation(t3);
  let t4;
  let t5;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <h1 className="mb-32 font-700 typo-h5">가구원동의 서비스 이용을 위한<br />제3자 제공 동의</h1>;
    t5 = <p className="mb-8 font-700 text-Gray-800 typo-body3">[한국장학재단 가구원 정보 확인 서비스]</p>;
    $[16] = t4;
    $[17] = t5;
  } else {
    t4 = $[16];
    t5 = $[17];
  }
  let t6;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <li className="list-disc">개인정보를 제공받는 자: <strong>한국장학재단</strong></li>;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  let t7;
  let t8;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <li className="list-disc">제공받는 자의 이용 목적:{" "}<strong>학자금 지원 구간 산정을 위한 가구원 정보 확인</strong></li>;
    t8 = <li className="list-disc">제공하는 개인정보 항목: CI(연계정보)</li>;
    $[19] = t7;
    $[20] = t8;
  } else {
    t7 = $[19];
    t8 = $[20];
  }
  let t9;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <div className="flex-1 px-20 pt-16">{t4}{t5}<ul className="pl-20 text-Gray-800 typo-body4">{t6}{t7}{t8}<li className="list-disc">제공받는 자의 보유 및 이용 기한:{" "}<strong>가구원 정보 확인 목적 달성 시까지 또는 관련 법령에 따른 보관 의무 기간</strong></li><li className="list-disc">동의 거부 권리 및 불이익: 개인정보 제3자 제공에 동의하지 않을 경우 '한국장학재단 가구원 정보 확인' 서비스 이용이 불가 합니다.</li></ul></div>;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== requestKosafConfirmUrl) {
    t10 = () => requestKosafConfirmUrl();
    $[22] = requestKosafConfirmUrl;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== isPending || $[25] !== t10) {
    t11 = <div className="flex h-full flex-col">{t9}<footer className="sticky bottom-0 bg-Gray-white px-20 py-16"><Button className="w-full" onClick={t10} disabled={isPending} loading={isPending}>동의하고 신청하기</Button></footer></div>;
    $[24] = isPending;
    $[25] = t10;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  return t11;
};
function _temp(state) {
  return state.webviewType === WEBVIEW_TYPE.KB_PAY;
}
function _temp2(state_0) {
  return state_0.osType;
}